/**
 * Member Portal API Service
 * Handles all API communication for the member portal
 */

import type {
  AIAnalysisResult,
  AIJobStatusResponse,
  ApiError,
  AssessmentDetail,
  AssessmentListItem,
  AuthTokens,
  CreateAssessmentRequest,
  CreateProgressNoteRequest,
  CreateRoutineRequest,
  DashboardSummary,
  DashboardWidgetData,
  LogRoutineCompletionRequest,
  LoginRequest,
  MemberProfile,
  Notification,
  ProductLog,
  ProductPurchase,
  ProgressNote,
  ProgressPhoto,
  Reminder,
  SignupRequest,
  SkinRoutine,
  SocialLoginRequest,
  SocialSignupRequest,
  User,
} from '../types/member';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const NORMALIZED_API_BASE_URL = API_BASE_URL.replace(/\/$/, '');
const MEMBER_API_BASE_URL = `${NORMALIZED_API_BASE_URL}/member`;

// ============================================================================
// HTTP CLIENT
// ============================================================================

class MemberApiClient {
  private baseUrl: string;
  private getToken: () => string | null;
  private refreshPromise: Promise<AuthTokens> | null = null;
  private onAuthFailure?: () => void;

  constructor(baseUrl: string, getToken: () => string | null) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
  }

  setOnAuthFailure(handler: () => void) {
    this.onAuthFailure = handler;
  }

  triggerAuthFailure(): void {
    this.onAuthFailure?.();
  }

  private async parseError(response: Response): Promise<MemberApiError> {
    let errorMessage = `Request failed with status ${response.status}`;
    let errorData: ApiError | undefined;
    try {
      const errorJson = await response.json();
      const detail = errorJson.detail;
      if (Array.isArray(detail)) {
        errorMessage = detail.map((d: any) => d.msg || String(d)).join('; ');
      } else {
        errorMessage = detail || errorJson.message || errorJson.error || errorMessage;
      }
      errorData = errorJson as ApiError;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    return new MemberApiError(errorMessage, response.status, errorData);
  }

  private async tryRefresh(): Promise<AuthTokens | null> {
    // If another request is already refreshing, wait for it
    if (this.refreshPromise) {
      try {
        return await this.refreshPromise;
      } catch {
        return null;
      }
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      this.onAuthFailure?.();
      return null;
    }

    this.refreshPromise = fetch(`${MEMBER_API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${refreshToken}`,
      },
      body: JSON.stringify({}),
    }).then(async (res) => {
      if (!res.ok) {
        const err = await this.parseError(res);
        throw err;
      }
      return res.json() as Promise<AuthTokens>;
    });

    try {
      const tokens = await this.refreshPromise;
      setAuthTokens(tokens);
      return tokens;
    } catch {
      clearAuthTokens();
      this.onAuthFailure?.();
      return null;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${MEMBER_API_BASE_URL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 by attempting token refresh
    if (response.status === 401) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        // Retry the original request with the new access token
        const retryResponse = await fetch(url, {
          ...options,
          headers: {
            ...headers,
            'Authorization': `Bearer ${refreshed.access_token}`,
          },
        });

        if (!retryResponse.ok) {
          throw await this.parseError(retryResponse);
        }

        if (retryResponse.status === 204) {
          return undefined as T;
        }
        return retryResponse.json();
      }

      // Refresh failed — session is dead
      const isAuthEndpoint = endpoint.includes('/auth/');
      if (!isAuthEndpoint) {
        this.onAuthFailure?.();
      }
      clearAuthTokens();
      throw await this.parseError(response);
    }

    if (!response.ok) {
      throw await this.parseError(response);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // HTTP methods
  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  post<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  patch<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export class MemberApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: ApiError
  ) {
    super(message);
    this.name = 'MemberApiError';
  }
}

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

const TOKEN_KEY = 'marple_member_token';
const REFRESH_TOKEN_KEY = 'marple_member_refresh_token';

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setAuthTokens(tokens: AuthTokens): void {
  localStorage.setItem(TOKEN_KEY, tokens.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
}

export function clearAuthTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

function decodeJwt(token: string): { sub?: string; exp?: number } | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch {
    return null;
  }
}

export function isTokenExpired(): boolean {
  const token = getAuthToken();
  if (!token) return true;
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.exp) return false; // assume valid if unparseable
  return decoded.exp * 1000 < Date.now();
}

export function isAuthenticated(): boolean {
  // Return true if we have either an access token or a refresh token,
  // so that auto-refresh has a chance to recover an expired access token.
  return !!getAuthToken() || !!getRefreshToken();
}

export function getUserIdFromToken(): string | null {
  const token = getAuthToken();
  if (!token) return null;
  const decoded = decodeJwt(token);
  return decoded?.sub || null;
}

// ============================================================================
// API CLIENT INSTANCE
// ============================================================================

const api = new MemberApiClient(API_BASE_URL, getAuthToken);

export function setOnAuthFailure(handler: () => void) {
  api.setOnAuthFailure(handler);
}

export function triggerAuthFailure(): void {
  api.triggerAuthFailure();
}

// ============================================================================
// AUTH API
// ============================================================================

export const authApi = {
  // Email signup
  signup: (data: SignupRequest) => 
    api.post<{ user: User; message: string; requires_verification: boolean }>('/auth/signup', data),

  // Social signup
  socialSignup: (data: SocialSignupRequest) =>
    api.post<{ user: User; message: string; requires_verification: boolean }>('/auth/signup/social', data),

  // Email login
  login: (data: LoginRequest) =>
    api.post<AuthTokens>('/auth/login', data),

  // Social login
  socialLogin: (data: SocialLoginRequest) =>
    api.post<AuthTokens>('/auth/login/social', data),

  // Verify email
  verifyEmail: (token: string) =>
    api.post<User>('/auth/verify-email', { token }),

  // Resend verification
  resendVerification: (email: string) =>
    api.post<{ message: string }>('/auth/resend-verification', { email }),

  // Refresh token
  refreshToken: () =>
    api.post<AuthTokens>('/auth/refresh', {}),

  // Logout
  logout: () =>
    api.post<void>('/auth/logout', {}),

  // Request password reset
  requestPasswordReset: (email: string) =>
    api.post<{ message: string }>('/auth/password-reset', { email }),

  // Confirm password reset
  confirmPasswordReset: (token: string, newPassword: string) =>
    api.post<{ message: string }>('/auth/password-reset/confirm', { token, new_password: newPassword }),
};

// ============================================================================
// PROFILE API
// ============================================================================

export const profileApi = {
  // Get my profile
  getProfile: () =>
    api.get<MemberProfile>('/profile/me'),

  // Update profile
  updateProfile: (data: Partial<MemberProfile>) =>
    api.patch<MemberProfile>('/profile/me', data),

  // Upload profile image
  uploadProfileImage: async (file: File): Promise<MemberProfile> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${MEMBER_API_BASE_URL}/profile/me/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload image');
    }

    return response.json();
  },

  // Delete profile image
  deleteProfileImage: () =>
    api.delete<MemberProfile>('/profile/me/image'),

  // Get profile completion
  getProfileCompletion: () =>
    api.get<{
      completion_percent: number;
      is_complete: boolean;
      missing_fields: string[];
      next_recommended_field: string | null;
    }>('/profile/me/completion'),

  // Get dashboard summary
  getDashboard: () =>
    api.get<DashboardSummary>('/profile/dashboard/summary'),

  // Get dashboard widgets
  getDashboardWidgets: () =>
    api.get<DashboardWidgetData>('/profile/dashboard/widgets'),

  // Request data export
  requestDataExport: () =>
    api.post<{ message: string; export_id: string }>('/profile/me/export-data', {}),

  // Request account deletion
  requestAccountDeletion: () =>
    api.post<{ message: string; deletion_date: string }>('/profile/me/delete-account', {}),

  // Cancel account deletion
  cancelAccountDeletion: () =>
    api.post<MemberProfile>('/profile/me/cancel-deletion', {}),

  // Activate profile with payment + holistic data
  activateProfile: (paymentReference: string, holisticData: Record<string, unknown>) =>
    api.post<{ activated: boolean; message: string }>('/profile/activate', {
      payment_reference: paymentReference,
      holistic_data: holisticData,
    }),
};

// ============================================================================
// AGENT API
// ============================================================================

export const agentApi = {
  getStatus: () =>
    api.get<{ agent_status: string | null; agent_code: string | null }>('/agent/status'),
};

// ============================================================================
// ASSESSMENTS API
// ============================================================================

export const assessmentsApi = {
  // List assessments
  listAssessments: (params?: { status?: string; limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    return api.get<AssessmentListItem[]>(`/assessments?${queryParams}`);
  },

  // Create assessment
  createAssessment: (data: CreateAssessmentRequest) =>
    api.post<AssessmentDetail>('/assessments', data),

  // Get assessment detail
  getAssessment: (id: string) =>
    api.get<AssessmentDetail>(`/assessments/${id}`),

  // Upload image
  uploadImage: async (assessmentId: string, imageType: string, file: File): Promise<{ id: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${MEMBER_API_BASE_URL}/assessments/${assessmentId}/images?image_type=${encodeURIComponent(imageType)}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      let errMsg = `Failed to upload ${imageType} image`;
      try {
        const errJson = await response.json();
        const detail = errJson.detail;
        errMsg = Array.isArray(detail) ? detail.map((d: any) => d.msg || String(d)).join('; ') : (detail || errMsg);
      } catch { /* ignore */ }
      throw new Error(errMsg);
    }

    return response.json();
  },

  // Submit assessment
  submitAssessment: (id: string) =>
    api.post<AIJobStatusResponse>(`/assessments/${id}/submit`, {}),

  // Get AI status
  getAIStatus: (assessmentId: string) =>
    api.get<AIJobStatusResponse>(`/assessments/${assessmentId}/ai-status`),

  // Get AI result
  getAIResult: (assessmentId: string) =>
    api.get<AIAnalysisResult>(`/assessments/${assessmentId}/ai-result`),

  // Delete assessment
  deleteAssessment: (id: string) =>
    api.delete<void>(`/assessments/${id}`),

  // Report paywall
  checkReportAccess: (id: string) =>
    api.get<{ has_access: boolean; paid_at?: string; price_cents: number; price_display: string }>(`/assessments/${id}/report-access`),
  payForReport: (id: string) =>
    api.post<{ status: string; message: string }>(`/assessments/${id}/pay-for-report`, {}),

  // Professional review request
  getReviewRequest: (id: string) =>
    api.get<{ exists: boolean; status?: string; price_cents: number; price_display: string; professional_id?: string; completed_at?: string; overall_assessment?: string; treatment_plan?: string; urgency_level?: string; pro_first_name?: string; pro_surname?: string; profession_type?: string; clinical_recommendations?: any[]; product_recommendations?: any[]; lifestyle_modifications?: any[]; differential_diagnoses?: any[]; follow_up_recommended?: boolean; review_submitted_at?: string }>(`/assessments/${id}/review-request`),
  requestProfessionalReview: (id: string) =>
    api.post<{ request_id: string; status: string; price_display: string; message: string }>(`/assessments/${id}/request-review`, {}),
};

// ============================================================================
// ROUTINES API
// ============================================================================

export const routinesApi = {
  // List routines
  listRoutines: (activeOnly = true) =>
    api.get<SkinRoutine[]>(`/routines?active_only=${activeOnly}`),

  // Create routine
  createRoutine: (data: CreateRoutineRequest) =>
    api.post<SkinRoutine>('/routines', data),

  // Get routine
  getRoutine: (id: string) =>
    api.get<SkinRoutine>(`/routines/${id}`),

  // Update routine
  updateRoutine: (id: string, data: Partial<CreateRoutineRequest>) =>
    api.patch<SkinRoutine>(`/routines/${id}`, data),

  // Delete routine
  deleteRoutine: (id: string) =>
    api.delete<void>(`/routines/${id}`),

  // Log routine completion
  logCompletion: (routineId: string, data: LogRoutineCompletionRequest) =>
    api.post<void>(`/routines/${routineId}/log`, data),

  // Get routine history
  getRoutineHistory: (routineId: string, params?: { start_date?: string; end_date?: string; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    return api.get<void>(`/routines/${routineId}/history?${queryParams}`);
  },

  // Get today's routine status
  getTodayStatus: () =>
    api.get<{
      date: string;
      morning: { total_steps: number; completed_steps: number; is_complete: boolean };
      evening: { total_steps: number; completed_steps: number; is_complete: boolean };
    }>('/routines/today/status'),
};

// ============================================================================
// PROGRESS API
// ============================================================================

export const progressApi = {
  // List photos
  listPhotos: (params?: { photo_type?: string; limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.photo_type) queryParams.append('photo_type', params.photo_type);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    return api.get<ProgressPhoto[]>(`/progress/photos?${queryParams}`);
  },

  // Upload photo
  uploadPhoto: async (photoType: string, photoDate: string, file: File, notes?: string, tags?: string[]): Promise<ProgressPhoto> => {
    const formData = new FormData();
    formData.append('file', file);

    const queryParams = new URLSearchParams();
    queryParams.append('photo_type', photoType);
    queryParams.append('photo_date', photoDate);
    if (notes) queryParams.append('notes', notes);
    if (tags) queryParams.append('tags', tags.join(','));

    const response = await fetch(
      `${MEMBER_API_BASE_URL}/progress/photos?${queryParams}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to upload photo');
    }

    return response.json();
  },

  // Delete photo
  deletePhoto: (id: string) =>
    api.delete<void>(`/progress/photos/${id}`),

  // List notes
  listNotes: (params?: { start_date?: string; end_date?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    return api.get<ProgressNote[]>(`/progress/notes?${queryParams}`);
  },

  // Create note
  createNote: (data: CreateProgressNoteRequest) =>
    api.post<ProgressNote>('/progress/notes', data),

  // Update note
  updateNote: (id: string, data: CreateProgressNoteRequest) =>
    api.patch<ProgressNote>(`/progress/notes/${id}`, data),

  // Delete note
  deleteNote: (id: string) =>
    api.delete<void>(`/progress/notes/${id}`),

  // Get timeline
  getTimeline: (params?: { start_date?: string; end_date?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    return api.get<Array<{
      type: 'photo' | 'note' | 'assessment';
      id: string;
      event_date: string;
      event_time: string;
      data: Record<string, unknown>;
    }>>(`/progress/timeline?${queryParams}`);
  },
};

// ============================================================================
// PRODUCTS API
// ============================================================================

export const productsApi = {
  // List purchases
  listPurchases: (params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    return api.get<ProductPurchase[]>(`/products/purchases?${queryParams}`);
  },

  // List product logs
  listProductLogs: (status?: string) => {
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    
    return api.get<ProductLog[]>(`/products/logs?${queryParams}`);
  },

  // Create product log
  createProductLog: (data: {
    product_name: string;
    purchase_id?: string;
    woocommerce_product_id?: number;
    product_sku?: string;
    status?: string;
    notes?: string;
  }) => api.post<ProductLog>('/products/logs', data),

  // Update product log
  updateProductLog: (id: string, data: Partial<ProductLog>) =>
    api.patch<ProductLog>(`/products/logs/${id}`, data),

  // Delete product log
  deleteProductLog: (id: string) =>
    api.delete<void>(`/products/logs/${id}`),

  // Get recommendations
  getRecommendations: () =>
    api.get<{
      based_on_profile: Record<string, unknown>;
      ai_recommended: unknown[];
      currently_using: unknown[];
      recommended_categories: unknown[];
    }>('/products/recommendations'),
};

// ============================================================================
// REMINDERS API
// ============================================================================

export const remindersApi = {
  // List reminders
  listReminders: (activeOnly = true) =>
    api.get<Reminder[]>(`/reminders?active_only=${activeOnly}`),

  // Create reminder
  createReminder: (data: Omit<Reminder, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<Reminder>('/reminders', data),

  // Get reminder
  getReminder: (id: string) =>
    api.get<Reminder>(`/reminders/${id}`),

  // Update reminder
  updateReminder: (id: string, data: Partial<Reminder>) =>
    api.patch<Reminder>(`/reminders/${id}`, data),

  // Snooze reminder
  snoozeReminder: (id: string, minutes: number) =>
    api.post<Reminder>(`/reminders/${id}/snooze`, { minutes }),

  // Delete reminder
  deleteReminder: (id: string) =>
    api.delete<void>(`/reminders/${id}`),

  // List notifications
  listNotifications: (unreadOnly = false, limit = 20) =>
    api.get<Notification[]>(`/reminders/notifications?unread_only=${unreadOnly}&limit=${limit}`),

  // Get unread count
  getUnreadCount: () =>
    api.get<{ unread_count: number }>('/reminders/notifications/unread-count'),

  // Mark notifications as read
  markNotificationsRead: (ids?: string[]) =>
    api.post<void>('/reminders/notifications/mark-read', { notification_ids: ids }),

  // Delete notification
  deleteNotification: (id: string) =>
    api.delete<void>(`/reminders/notifications/${id}`),

  // Setup default reminders
  setupDefaults: () =>
    api.post<{ message: string; created_ids: string[] }>('/reminders/setup-defaults', {}),
};

// ============================================================================
// POLLING HELPERS
// ============================================================================

export function pollAIStatus(
  assessmentId: string,
  onStatusChange: (status: AIJobStatusResponse) => void,
  onComplete: (result: AIAnalysisResult) => void,
  onError: (error: Error) => void,
  interval = 5000
): () => void {
  let isPolling = true;
  let timeoutId: number;
  let consecutiveErrors = 0;
  const MAX_ERRORS = 3;
  const MAX_POLLS = 120; // 10 minutes at 5s intervals
  let pollCount = 0;

  const poll = async () => {
    if (!isPolling) return;
    pollCount++;

    if (pollCount > MAX_POLLS) {
      onError(new Error('Analysis is taking longer than expected. Please check back later.'));
      return;
    }

    try {
      const status = await assessmentsApi.getAIStatus(assessmentId);
      consecutiveErrors = 0; // Reset on success

      onStatusChange(status);

      if (status.status === 'completed') {
        const result = await assessmentsApi.getAIResult(assessmentId);
        onComplete(result);
        return;
      }

      if (status.status === 'failed') {
        onError(new Error(status.failure_reason || 'Analysis failed'));
        return;
      }

      // Continue polling
      timeoutId = window.setTimeout(poll, interval);
    } catch (error) {
      consecutiveErrors++;
      const err = error instanceof Error ? error : new Error('Unknown error');

      // Stop immediately on auth errors (401)
      if (err instanceof MemberApiError && err.status === 401) {
        onError(new Error('Your session stepped away — sign back in to check your results.'));
        return;
      }

      // Allow a few transient errors before giving up
      if (consecutiveErrors >= MAX_ERRORS) {
        onError(err);
        return;
      }

      // Retry with backoff
      timeoutId = window.setTimeout(poll, interval * consecutiveErrors);
    }
  };

  poll();

  // Return cleanup function
  return () => {
    isPolling = false;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
}

// ============================================================================
// REPORT PAYMENTS & PROFESSIONAL REVIEW API
// ============================================================================

export const paymentsApi = {
  // Get system config (prices)
  getConfig: () =>
    fetch(`${NORMALIZED_API_BASE_URL}/system/config`)
      .then(r => r.json()) as Promise<{ report_price: string; professional_review_price: string }>,

  // Unlock AI report (R39.99) — submit EFT proof
  unlockReport: (assessmentId: string, paymentReference: string) =>
    api.post<{ id: string; payment_status: string; message: string }>(
      `/assessments/${assessmentId}/unlock-report`,
      { payment_method: 'eft', payment_reference: paymentReference }
    ),

  // Request professional review (R400)
  requestProfessionalReview: (assessmentId: string, paymentReference: string) =>
    api.post<{ id: string; status: string; message: string }>(
      `/assessments/${assessmentId}/request-professional-review`,
      { payment_method: 'eft', payment_reference: paymentReference }
    ),
};

// ============================================================================
// NOTIFICATIONS API
// ============================================================================

export const notificationsApi = {
  list: (params?: { unread_only?: boolean; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.unread_only) q.append('unread_only', 'true');
    if (params?.limit) q.append('limit', params.limit.toString());
    return api.get<import('../types/member').Notification[]>(`/notifications?${q}`);
  },

  markRead: (id: string) =>
    api.post<void>(`/notifications/${id}/read`, {}),

  markAllRead: () =>
    api.post<void>('/notifications/read-all', {}),
};

// ============================================================================
// GENERIC AUTHENTICATED FETCH HELPER
// ============================================================================

/**
 * Authenticated fetch wrapper for ad-hoc requests not covered by the typed API.
 * Automatically attaches the Bearer token and parses JSON.
 * Throws an Error with message set to the `detail` field on non-2xx responses.
 */
function isMemberApiUrl(url: string): boolean {
  return url.startsWith(MEMBER_API_BASE_URL + '/');
}

export async function memberApiFetch(url: string, options: RequestInit = {}): Promise<any> {
  // Route member API calls through the client so 401/refresh handling works
  if (isMemberApiUrl(url)) {
    const endpoint = url.slice(MEMBER_API_BASE_URL.length);
    return api.request(endpoint, options);
  }

  const token = getAuthToken();
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = body?.detail;
    const message = Array.isArray(detail)
      ? detail.map((d: any) => d.msg || String(d)).join('; ')
      : detail || body?.message || `Request failed (${res.status})`;
    throw Object.assign(new Error(message), { status: res.status });
  }
  if (res.status === 204) return undefined;
  return res.json();
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  auth: authApi,
  profile: profileApi,
  agent: agentApi,
  assessments: assessmentsApi,
  routines: routinesApi,
  progress: progressApi,
  products: productsApi,
  reminders: remindersApi,
  pollAIStatus,
};
